import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft,
  ArrowRight,
  Coffee,
  Mountain,
  Building2,
  Palmtree,
  Umbrella,
  Landmark,
  Tent,
  User,
  Users,
  Baby,
  Dog,
  Wallet,
  CreditCard,
  Gem,
  Check,
  Loader2,
  Sparkles,
  Home,
  Bookmark,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// Panchi Bird Logo
const PanchiBirdLogo = ({ className = "w-10 h-10" }) => (
  <img 
    src="https://customer-assets.emergentagent.com/job_panchi-travel-1/artifacts/5hhgrsb0_20260119_170240_0000.png"
    alt="Panchi"
    className={className}
    style={{ objectFit: 'contain' }}
  />
);

const TRAVEL_TYPES = [
  { value: 'beach', label: 'Beach', icon: Umbrella, color: 'teal' },
  { value: 'mountain', label: 'Mountains', icon: Mountain, color: 'emerald' },
  { value: 'city', label: 'City Explorer', icon: Building2, color: 'cyan' },
  { value: 'cultural', label: 'Heritage', icon: Landmark, color: 'amber' },
  { value: 'adventure', label: 'Adventure', icon: Tent, color: 'orange' },
  { value: 'relaxation', label: 'Relaxation', icon: Coffee, color: 'rose' },
  { value: 'nature', label: 'Nature', icon: Palmtree, color: 'green' },
  { value: 'spiritual', label: 'Spiritual', emoji: '🙏', color: 'purple' },
];

const COMPANION_PREFS = [
  { value: 'solo', label: 'Solo', icon: User, color: 'teal' },
  { value: 'couple', label: 'Couple', emoji: '💑', color: 'rose' },
  { value: 'family', label: 'Family', icon: Users, color: 'cyan' },
  { value: 'family_kids', label: 'With Kids', icon: Baby, color: 'amber' },
  { value: 'friends', label: 'Friends', emoji: '👯', color: 'emerald' },
  { value: 'pets', label: 'With Pets', icon: Dog, color: 'orange' },
];

const BUDGET_LEVELS = [
  { value: 'budget', label: 'Budget', range: '$500 - $1,500', icon: Wallet, color: 'emerald', desc: 'Smart & affordable' },
  { value: 'comfort', label: 'Comfort', range: '$1,500 - $4,000', icon: CreditCard, color: 'teal', desc: 'Balance of value & comfort' },
  { value: 'luxury', label: 'Luxury', range: '$4,000+', icon: Gem, color: 'amber', desc: 'Premium experiences' },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, completeOnboarding } = useAuth();
  
  const [step, setStep] = useState(1);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedCompanions, setSelectedCompanions] = useState([]);
  const [budgetLevel, setBudgetLevel] = useState(null);
  const [homeCity, setHomeCity] = useState('');
  const [saving, setSaving] = useState(false);
  
  const totalSteps = 4;
  
  const toggleSelection = (value, list, setter, max = 3) => {
    if (list.includes(value)) {
      setter(list.filter(v => v !== value));
    } else if (list.length < max) {
      setter([...list, value]);
    } else {
      toast.info(`You can select up to ${max} options`);
    }
  };
  
  const handleComplete = async () => {
    try {
      setSaving(true);
      
      const travelDNA = {
        travel_types: selectedTypes,
        companion_preferences: selectedCompanions,
        budget_level: budgetLevel,
        home_city: homeCity,
      };
      
      await api.saveTravelDNA(user?.user_id || 'default', travelDNA);
      await completeOnboarding();
      
      toast.success('Travel DNA saved! 🎉');
      navigate('/');
    } catch (error) {
      toast.error('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  const canProceed = () => {
    switch (step) {
      case 1: return selectedTypes.length > 0;
      case 2: return selectedCompanions.length > 0;
      case 3: return budgetLevel !== null;
      case 4: return homeCity.trim().length > 0;
      default: return true;
    }
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-50 via-white to-teal-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 md:px-6 py-3 flex items-center justify-between">
          <button 
            onClick={() => {
              if (step > 1) {
                setStep(step - 1);
              } else {
                // Always navigate to home page
                navigate('/');
              }
            }}
            className="flex items-center gap-2 text-gray-500 hover:text-teal-600 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          
          <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity" data-testid="logo-link">
            <PanchiBirdLogo className="w-8 h-8" />
            <span className="font-bold text-gray-900">Travel DNA</span>
          </Link>
          
          {/* Progress */}
          <div className="flex items-center gap-1.5">
            {[1, 2, 3, 4].map((s) => (
              <div
                key={s}
                className={`h-2 transition-all duration-300 ${
                  s <= step 
                    ? 'bg-gradient-to-r from-teal-400 to-cyan-500 w-6' 
                    : 'bg-gray-200 w-2'
                }`}
                style={{ borderRadius: '9999px' }}
              />
            ))}
          </div>
        </div>
      </header>
      
      <main className="max-w-2xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <AnimatePresence mode="wait">
          {/* Step 1: Travel Types */}
          {step === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="space-y-8"
            >
              <div className="text-center mb-8">
                <motion.p 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-teal-600 text-sm font-bold tracking-wider uppercase mb-3"
                >
                  Step 1 of {totalSteps}
                </motion.p>
                <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
                  Your Travel <span className="text-teal-600">Style</span>
                </h1>
                <p className="text-gray-500 text-lg">
                  Select up to 3 types of trips you love
                </p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {TRAVEL_TYPES.map((type) => {
                  const isSelected = selectedTypes.includes(type.value);
                  return (
                    <button
                      key={type.value}
                      onClick={() => toggleSelection(type.value, selectedTypes, setSelectedTypes)}
                      className={`relative p-4 bg-white/80 backdrop-blur border-2 text-center transition-all ${
                        isSelected ? 'border-teal-400 bg-teal-50 shadow-md' : 'border-gray-100 hover:border-gray-200'
                      }`}
                      style={{ borderRadius: '1rem' }}
                      data-testid={`type-${type.value}`}
                    >
                      <div className={`w-12 h-12 mx-auto flex items-center justify-center mb-2 ${
                        isSelected ? 'bg-teal-100' : 'bg-gray-100'
                      }`} style={{ borderRadius: '0.75rem' }}>
                        {type.icon ? (
                          <type.icon size={24} className={isSelected ? 'text-teal-600' : 'text-gray-500'} />
                        ) : (
                          <span className="text-2xl">{type.emoji}</span>
                        )}
                      </div>
                      <span className={`block font-semibold ${isSelected ? 'text-teal-700' : 'text-gray-900'}`}>
                        {type.label}
                      </span>
                      {isSelected && (
                        <div className="absolute top-2 right-2">
                          <Check size={16} className="text-teal-600" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setStep(2)}
                disabled={!canProceed()}
                className={`w-full flex items-center justify-center gap-2 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold text-lg shadow-xl transition-all ${
                  canProceed() ? 'shadow-teal-500/30 hover:shadow-teal-500/50 hover:scale-[1.02]' : 'opacity-50 cursor-not-allowed'
                }`}
                style={{ borderRadius: '9999px' }}
                data-testid="next-step-btn"
              >
                Continue
                <ArrowRight size={20} />
              </button>
            </motion.div>
          )}
          
          {/* Step 2: Companions */}
          {step === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="space-y-8"
            >
              <div className="text-center mb-8">
                <p className="text-teal-600 text-sm font-bold tracking-wider uppercase mb-3">
                  Step 2 of {totalSteps}
                </p>
                <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
                  Travel <span className="text-teal-600">Companions</span>
                </h1>
                <p className="text-gray-500 text-lg">
                  Who do you usually travel with?
                </p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {COMPANION_PREFS.map((comp) => {
                  const isSelected = selectedCompanions.includes(comp.value);
                  return (
                    <button
                      key={comp.value}
                      onClick={() => toggleSelection(comp.value, selectedCompanions, setSelectedCompanions, 2)}
                      className={`relative p-4 bg-white/80 backdrop-blur border-2 text-center transition-all ${
                        isSelected ? 'border-teal-400 bg-teal-50 shadow-md' : 'border-gray-100 hover:border-gray-200'
                      }`}
                      style={{ borderRadius: '1rem' }}
                      data-testid={`companion-${comp.value}`}
                    >
                      <div className={`w-12 h-12 mx-auto flex items-center justify-center mb-2 ${
                        isSelected ? 'bg-teal-100' : 'bg-gray-100'
                      }`} style={{ borderRadius: '0.75rem' }}>
                        {comp.icon ? (
                          <comp.icon size={24} className={isSelected ? 'text-teal-600' : 'text-gray-500'} />
                        ) : (
                          <span className="text-2xl">{comp.emoji}</span>
                        )}
                      </div>
                      <span className={`block font-semibold ${isSelected ? 'text-teal-700' : 'text-gray-900'}`}>
                        {comp.label}
                      </span>
                      {isSelected && (
                        <div className="absolute top-2 right-2">
                          <Check size={16} className="text-teal-600" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setStep(3)}
                disabled={!canProceed()}
                className={`w-full flex items-center justify-center gap-2 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold text-lg shadow-xl transition-all ${
                  canProceed() ? 'shadow-teal-500/30 hover:shadow-teal-500/50 hover:scale-[1.02]' : 'opacity-50 cursor-not-allowed'
                }`}
                style={{ borderRadius: '9999px' }}
                data-testid="next-step-btn"
              >
                Continue
                <ArrowRight size={20} />
              </button>
            </motion.div>
          )}
          
          {/* Step 3: Budget */}
          {step === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="space-y-8"
            >
              <div className="text-center mb-8">
                <p className="text-teal-600 text-sm font-bold tracking-wider uppercase mb-3">
                  Step 3 of {totalSteps}
                </p>
                <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
                  Your <span className="text-teal-600">Budget</span> Style
                </h1>
                <p className="text-gray-500 text-lg">
                  {"What's your typical trip budget?"}
                </p>
              </div>
              
              <div className="space-y-3">
                {BUDGET_LEVELS.map((budget) => {
                  const isSelected = budgetLevel === budget.value;
                  return (
                    <button
                      key={budget.value}
                      onClick={() => setBudgetLevel(budget.value)}
                      className={`w-full flex items-center gap-4 p-5 bg-white/80 backdrop-blur border-2 text-left transition-all ${
                        isSelected ? 'border-teal-400 bg-teal-50 shadow-md' : 'border-gray-100 hover:border-gray-200'
                      }`}
                      style={{ borderRadius: '1.25rem' }}
                      data-testid={`budget-${budget.value}`}
                    >
                      <div className={`w-14 h-14 flex items-center justify-center ${
                        isSelected ? 'bg-teal-100' : 'bg-gray-100'
                      }`} style={{ borderRadius: '1rem' }}>
                        <budget.icon size={28} className={isSelected ? 'text-teal-600' : 'text-gray-500'} />
                      </div>
                      <div className="flex-1">
                        <p className={`font-bold text-lg ${isSelected ? 'text-teal-700' : 'text-gray-900'}`}>
                          {budget.label}
                        </p>
                        <p className="text-sm text-gray-500">{budget.desc}</p>
                        <p className={`text-sm font-semibold ${isSelected ? 'text-teal-600' : 'text-gray-600'}`}>
                          {budget.range}
                        </p>
                      </div>
                      {isSelected && (
                        <div className="w-8 h-8 bg-teal-500 flex items-center justify-center" style={{ borderRadius: '50%' }}>
                          <Check size={18} className="text-white" />
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => setStep(4)}
                disabled={!canProceed()}
                className={`w-full flex items-center justify-center gap-2 py-4 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold text-lg shadow-xl transition-all ${
                  canProceed() ? 'shadow-teal-500/30 hover:shadow-teal-500/50 hover:scale-[1.02]' : 'opacity-50 cursor-not-allowed'
                }`}
                style={{ borderRadius: '9999px' }}
                data-testid="next-step-btn"
              >
                Continue
                <ArrowRight size={20} />
              </button>
            </motion.div>
          )}
          
          {/* Step 4: Home City */}
          {step === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -50 }}
              className="space-y-8"
            >
              <div className="text-center mb-8">
                <p className="text-teal-600 text-sm font-bold tracking-wider uppercase mb-3">
                  Step 4 of {totalSteps}
                </p>
                <h1 className="text-3xl md:text-4xl font-black text-gray-900 mb-3">
                  Your <span className="text-teal-600">Home</span> City
                </h1>
                <p className="text-gray-500 text-lg">
                  Where do you usually depart from?
                </p>
              </div>
              
              <div className="p-6 bg-white/80 backdrop-blur border border-gray-100 shadow-lg" style={{ borderRadius: '1.5rem' }}>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-teal-100 flex items-center justify-center" style={{ borderRadius: '0.75rem' }}>
                    <MapPin size={20} className="text-teal-600" />
                  </div>
                  <span className="text-gray-600 font-medium">Home City</span>
                </div>
                
                <input
                  type="text"
                  value={homeCity}
                  onChange={(e) => setHomeCity(e.target.value)}
                  placeholder="e.g., New York, London, Mumbai..."
                  className="w-full pb-3 bg-transparent border-b-2 border-gray-200 text-2xl font-bold text-gray-900 placeholder-gray-300 focus:border-teal-500 outline-none transition-colors"
                  autoFocus
                  data-testid="home-city-input"
                />
              </div>
              
              <button
                onClick={handleComplete}
                disabled={!canProceed() || saving}
                className={`w-full flex items-center justify-center gap-2 py-5 bg-gradient-to-r from-teal-500 to-cyan-500 text-white font-bold text-lg shadow-xl transition-all ${
                  canProceed() && !saving ? 'shadow-teal-500/30 hover:shadow-teal-500/50 hover:scale-[1.02]' : 'opacity-50 cursor-not-allowed'
                }`}
                style={{ borderRadius: '9999px' }}
                data-testid="complete-btn"
              >
                {saving ? (
                  <>
                    <Loader2 size={22} className="animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Sparkles size={22} />
                    Complete My Travel DNA
                  </>
                )}
              </button>
              
              <p className="text-center text-sm text-gray-400">
                You can update these preferences anytime from your profile.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
